import React, { createContext, useContext, useState, useEffect } from "react";
import backendApi from "../api/backendApi";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  // Requirement 8: Load user from localStorage FIRST (optimistic load)
  // This prevents a flash of the loading screen for users with valid sessions
  const [user, setUser] = useState(() => {
    try {
      const stored = localStorage.getItem("user");
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Requirement 5: loading = true until auth check completes
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Load token and user from localStorage
        const rawToken = localStorage.getItem("token");
        const validToken = rawToken && rawToken !== "undefined" && rawToken !== "null";

        if (!validToken) {
          // Clean up any stale/corrupted values
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
          setLoading(false);
          return;
        }

        // Token exists, optimistically keep the user from localStorage.
        // We already loaded the user via useState initializer above.
        // Silently verify in background WITHOUT clearing storage on failure.
        try {
          const userData = await backendApi.auth.me();
          if (userData) {
            setUser(userData);
            localStorage.setItem("user", JSON.stringify(userData));
          }
          // me() returning null (network error, 500) = keep existing user
        } catch {
          // me() threw (401) but we DO NOT clear localStorage here.
          // If the token is truly expired the next API call (e.g. save profile)
          // will fail with 401 and THAT catch block will handle the redirect.
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);


  // Requirement 7: Store token + user on login
  const login = (userData) => {
    setUser(userData);
    localStorage.setItem("user", JSON.stringify(userData));
  };

  // Requirement: Logout clears state and storage, React Router handles navigation
  const logout = () => {
    backendApi.auth.logout();
    setUser(null);
  };

  const updateUser = (data) => {
    setUser(prev => {
      const updated = prev ? { ...prev, ...data } : null;
      if (updated) {
        localStorage.setItem("user", JSON.stringify(updated));
      }
      return updated;
    });
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
